import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Package, Edit, Trash2, 
  User, Folder, Eye
} from 'lucide-react';
import { supabase } from '../config/supabase';
import { cn } from '../utils/cn';
import { useUserStore } from '../stores/user-store';
import ConfirmDialog from '../components/ConfirmDialog';
import { PERMISSION_LABELS, PERMISSION_COLORS } from '../utils/permissions';
import { PermissionLevel } from '../types';

interface Order {
  id: string;
  order_number: string;
  description?: string;
  folder_name: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  is_active: boolean;
  primary_category_id?: string;
  auto_create_folder: boolean;
  // 关联信息
  file_count?: number;
  access_count?: number;
  total_size?: number;
  primary_folder_name?: string;
  // 创建者信息
  creator_name?: string;
  creator_email?: string;
}

interface AccessRecord {
  user_id: string;
  accessed_at: string;
  ip_address?: string;
  name?: string;
  email: string;
  role: string;
}

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);
  const [accessRecords, setAccessRecords] = useState<AccessRecord[]>([]);
  const [isLoadingAccess, setIsLoadingAccess] = useState(false);
  const { profile } = useUserStore();
  const [pageConfirm, setPageConfirm] = useState<{
    open: boolean; title: string; message: string;
    variant: 'danger' | 'warning' | 'default'; confirmText: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', variant: 'default', confirmText: '确定', onConfirm: () => {} });

  // 新建订单表单
  const [newOrder, setNewOrder] = useState({
    order_number: '',
    description: '',
    folder_name: '',
    expires_at: '',
    auto_create_folder: true
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      
      // 构建查询，包含创建者信息和统计数据
      let query = supabase
        .from('orders')
        .select(`
          *,
          creator:users!created_by(
            name,
            email
          ),
          order_files(count),
          order_user_access(count)
        `)
        .eq('is_active', true);
      
      // 如果是uploader角色，只显示自己创建的订单
      if (profile?.role === 'uploader') {
        query = query.eq('created_by', profile.id);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      
      // 转换数据格式
      const transformedOrders = (data || []).map(order => ({
        ...order,
        creator_name: order.creator?.name || null,
        creator_email: order.creator?.email || null,
        file_count: order.order_files?.[0]?.count || 0,
        access_count: order.order_user_access?.[0]?.count || 0
      }));
      
      setOrders(transformedOrders);
    } catch (error) {
      console.error('获取订单失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrders = orders.filter(order =>
    order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    order.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newOrder.order_number.trim()) {
      alert('请输入订单号');
      return;
    }

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('用户未登录');

      const { error } = await supabase
        .from('orders')
        .insert({
          order_number: newOrder.order_number.trim(),
          description: newOrder.description.trim() || `订单 - ${newOrder.order_number.trim()}`,
          folder_name: newOrder.folder_name.trim() || newOrder.order_number.trim(),
          created_by: user.user.id,
          expires_at: newOrder.expires_at || null,
          auto_create_folder: newOrder.auto_create_folder,
          is_active: true
        });

      if (error) {
        if (error.code === '23505') {
          alert('订单号已存在，请使用不同的订单号');
        } else {
          throw error;
        }
        return;
      }

      alert('订单创建成功！');
      setShowCreateModal(false);
      setNewOrder({
        order_number: '',
        description: '',
        folder_name: '',
        expires_at: '',
        auto_create_folder: true
      });
      fetchOrders();
    } catch (error) {
      console.error('创建订单失败:', error);
      alert('创建订单失败，请重试');
    }
  };

  const handleToggleStatus = async (orderId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ is_active: !currentStatus })
        .eq('id', orderId);

      if (error) throw error;
      
      alert(`订单已${currentStatus ? '禁用' : '启用'}`);
      fetchOrders();
    } catch (error) {
      console.error('更新订单状态失败:', error);
      alert('更新订单状态失败，请重试');
    }
  };

  const handleDeleteOrder = (orderId: string, orderNumber: string) => {
    setPageConfirm({
      open: true, title: '删除订单', variant: 'danger', confirmText: '删除',
      message: `确定要删除订单 ${orderNumber} 吗？此操作不可撤销。`,
      onConfirm: async () => {
        setPageConfirm(p => ({ ...p, open: false }));
        try {
          const { error } = await supabase.from('orders').delete().eq('id', orderId);
          if (error) throw error;
          fetchOrders();
        } catch (error) {
          console.error('删除订单失败:', error);
        }
      },
    });
  };

  const handleEditOrder = (order: Order) => {
    setEditingOrder(order);
    setShowEditModal(true);
  };

  const handleUpdateOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingOrder) return;

    try {
      const { error } = await supabase
        .from('orders')
        .update({
          order_number: editingOrder.order_number.trim(),
          description: editingOrder.description?.trim() || null,
          folder_name: editingOrder.folder_name.trim(),
          expires_at: editingOrder.expires_at || null,
          auto_create_folder: editingOrder.auto_create_folder,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingOrder.id);

      if (error) {
        if (error.code === '23505') {
          alert('订单号已存在，请使用不同的订单号');
        } else {
          throw error;
        }
        return;
      }

      alert('订单更新成功！');
      setShowEditModal(false);
      setEditingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('更新订单失败:', error);
      alert('更新订单失败，请重试');
    }
  };

  const fetchAccessRecords = async (orderId: string) => {
    try {
      setIsLoadingAccess(true);
      
      // 首先获取访问记录
      const { data: accessData, error: accessError } = await supabase
        .from('order_user_access')
        .select('user_id, accessed_at, ip_address')
        .eq('order_id', orderId)
        .order('accessed_at', { ascending: false });

      if (accessError) throw accessError;

      if (!accessData || accessData.length === 0) {
        setAccessRecords([]);
        return;
      }

      // 尝试获取用户信息（RLS策略修复后应该能成功）
      const userIds = accessData.map(record => record.user_id);
      const { data: usersData, error: usersError } = await supabase
        .from('v_fs_users')
        .select('id, name, email, role')
        .in('id', userIds);

      if (usersError) {
        console.warn('获取用户信息失败，使用ID显示:', usersError);
      }

      // 合并数据
      const records: AccessRecord[] = accessData.map(accessRecord => {
        const user = usersData?.find(u => u.id === accessRecord.user_id);
        return {
          user_id: accessRecord.user_id,
          accessed_at: accessRecord.accessed_at,
          ip_address: accessRecord.ip_address,
          name: user?.name || undefined,
          email: user?.email || `ID: ${accessRecord.user_id}`,
          role: user?.role || 'unknown'
        };
      });
      
      setAccessRecords(records);
    } catch (error) {
      console.error('获取访问记录失败:', error);
      setAccessRecords([]);
    } finally {
      setIsLoadingAccess(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <Package className="w-6 h-6 mr-2 text-orange-600" />
          订单管理
        </h1>
        <p className="text-gray-600">管理系统中的所有订单，支持创建、编辑和删除订单</p>
      </div>

      {/* 搜索和操作栏 */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="搜索订单号或描述..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          新建订单
        </button>
      </div>

      {/* 订单列表 */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    订单信息
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    文件夹
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    统计
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    创建时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">
                            {order.order_number}
                          </div>
                          {order.creator_name && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <User className="w-3 h-3 mr-1" />
                              {order.creator_name}
                            </span>
                          )}
                        </div>
                        {order.description && (
                          <div className="text-sm text-gray-500 max-w-xs truncate">
                            {order.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Folder className="w-4 h-4 text-blue-500 mr-2" />
                        <span className="text-sm text-gray-900">
                          {order.primary_folder_name || order.folder_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.file_count || 0} 文件
                      </div>
                      <div className="text-sm text-gray-500">
                        <span className={cn(
                          "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                          (order.access_count || 0) > 0 
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        )}>
                          {order.access_count || 0} 次查询
                        </span>
                      </div>
                      {order.total_size && (
                        <div className="text-sm text-gray-500">
                          {formatFileSize(order.total_size)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleStatus(order.id, order.is_active)}
                        className={cn(
                          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                          order.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        )}
                      >
                        {order.is_active ? '有效' : '已禁用'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDetailsModal(true);
                            fetchAccessRecords(order.id);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="查看详情"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditOrder(order)}
                          className="text-orange-600 hover:text-orange-900"
                          title="编辑订单"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteOrder(order.id, order.order_number)}
                          className="text-red-600 hover:text-red-900"
                          title="删除订单"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredOrders.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? '没有找到匹配的订单' : '暂无订单'}
              </h3>
              <p className="text-gray-500">
                {searchQuery ? '尝试调整搜索条件' : '点击"新建订单"按钮创建第一个订单'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* 新建订单模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">新建订单</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleCreateOrder} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订单号 *
                </label>
                <input
                  type="text"
                  value={newOrder.order_number}
                  onChange={(e) => setNewOrder({...newOrder, order_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="输入唯一的订单号"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订单描述
                </label>
                <textarea
                  value={newOrder.description}
                  onChange={(e) => setNewOrder({...newOrder, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                  placeholder="订单的详细描述（可选）"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  文件夹名称
                </label>
                <input
                  type="text"
                  value={newOrder.folder_name}
                  onChange={(e) => setNewOrder({...newOrder, folder_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="留空则使用订单号作为文件夹名"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  过期时间（可选）
                </label>
                <input
                  type="datetime-local"
                  value={newOrder.expires_at}
                  onChange={(e) => setNewOrder({...newOrder, expires_at: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="auto_create_folder"
                  checked={newOrder.auto_create_folder}
                  onChange={(e) => setNewOrder({...newOrder, auto_create_folder: e.target.checked})}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="auto_create_folder" className="ml-2 text-sm text-gray-700">
                  查询时自动创建文件夹
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
                >
                  创建订单
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑订单模态框 */}
      {showEditModal && editingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">编辑订单</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingOrder(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleUpdateOrder} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订单号 *
                </label>
                <input
                  type="text"
                  value={editingOrder.order_number}
                  onChange={(e) => setEditingOrder({...editingOrder, order_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="输入唯一的订单号"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  订单描述
                </label>
                <textarea
                  value={editingOrder.description || ''}
                  onChange={(e) => setEditingOrder({...editingOrder, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={3}
                  placeholder="订单的详细描述（可选）"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  文件夹名称 *
                </label>
                <input
                  type="text"
                  value={editingOrder.folder_name}
                  onChange={(e) => setEditingOrder({...editingOrder, folder_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="文件夹名称"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  过期时间（可选）
                </label>
                <input
                  type="datetime-local"
                  value={editingOrder.expires_at ? new Date(editingOrder.expires_at).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditingOrder({...editingOrder, expires_at: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit_auto_create_folder"
                  checked={editingOrder.auto_create_folder}
                  onChange={(e) => setEditingOrder({...editingOrder, auto_create_folder: e.target.checked})}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="edit_auto_create_folder" className="ml-2 text-sm text-gray-700">
                  查询时自动创建文件夹
                </label>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingOrder(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
                >
                  保存更改
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 订单详情模态框 */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">订单详情</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">订单号</label>
                  <p className="text-lg font-semibold text-gray-900">{selectedOrder.order_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">状态</label>
                  <p className={cn(
                    "text-sm font-medium",
                    selectedOrder.is_active ? "text-green-600" : "text-red-600"
                  )}>
                    {selectedOrder.is_active ? '有效' : '已禁用'}
                  </p>
                </div>
              </div>
              
              {selectedOrder.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">描述</label>
                  <p className="text-gray-900">{selectedOrder.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">文件夹名称</label>
                  <p className="text-gray-900">{selectedOrder.folder_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">自动创建文件夹</label>
                  <p className="text-gray-900">{selectedOrder.auto_create_folder ? '是' : '否'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">文件数量</label>
                  <p className="text-lg font-semibold text-blue-600">{selectedOrder.file_count || 0}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">查询次数</label>
                  <p className="text-lg font-semibold text-green-600">{selectedOrder.access_count || 0}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">总大小</label>
                  <p className="text-lg font-semibold text-purple-600">
                    {selectedOrder.total_size ? formatFileSize(selectedOrder.total_size) : '0 B'}
                  </p>
                </div>
              </div>
              
              {/* 查询用户详情 */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">查询用户详情</h3>
                  <span className="text-sm text-gray-500">
                    共 {accessRecords.length} 个用户查询过此订单
                    {accessRecords.length > 3 && <span className="ml-1 text-xs text-gray-400">（可滚动查看）</span>}
                  </span>
                </div>
                
                {isLoadingAccess ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  </div>
                ) : accessRecords.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto border border-gray-200 scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
                    <div className="space-y-2">
                      {accessRecords.map((record) => (
                        <div key={record.user_id + record.accessed_at} className="bg-white rounded-lg p-2.5 border border-gray-200 shadow-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-900">
                                  {record.name || `用户 ${record.user_id.slice(-8)}`}
                                </span>
                                <span className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                                  PERMISSION_COLORS[record.role as PermissionLevel] || "bg-gray-100 text-gray-800"
                                )}>
                                  {PERMISSION_LABELS[record.role as PermissionLevel] || record.role}
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                {record.email && !record.email.startsWith('ID:') ? record.email : `ID: ${record.user_id}`}
                              </div>
                            </div>
                            <div className="text-right text-sm text-gray-500">
                              <div>{formatDate(record.accessed_at)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p>还没有用户查询过此订单</p>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">创建时间</label>
                  <p className="text-gray-900">{formatDate(selectedOrder.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">更新时间</label>
                  <p className="text-gray-900">{formatDate(selectedOrder.updated_at)}</p>
                </div>
              </div>
              
              {selectedOrder.expires_at && (
                <div>
                  <label className="text-sm font-medium text-gray-500">过期时间</label>
                  <p className="text-gray-900">{formatDate(selectedOrder.expires_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 通用确认弹窗 */}
      <ConfirmDialog
        open={pageConfirm.open}
        title={pageConfirm.title}
        message={pageConfirm.message}
        variant={pageConfirm.variant}
        confirmText={pageConfirm.confirmText}
        onConfirm={pageConfirm.onConfirm}
        onCancel={() => setPageConfirm(p => ({ ...p, open: false }))}
      />
    </div>
  );
};

export default OrdersPage;
